import { type ReactNode, useId, useState } from 'react';
import type { EligibilityCurrentResponse } from '@features/patient-coverages/types';
import { formatUsdCents } from '@features/patient-coverages/utils/patientCost';
import {
  CheckCircleOutlineRounded,
  HighlightOffRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import { Box, Button, Collapse, Stack, Typography } from '@mui/material';

import { ELIG_TOKENS } from '../theme/readinessTokens';
import { FINANCIAL_FIELDS, type FinancialFieldDef } from '../utils/verificationFinancials';

import { CoverageSourceDetails } from './CoverageSourceDetails';

const primary = ['copayment', 'coinsurance', 'remaining_deductible', 'remaining_oop_max'];
const outcomes = {
  active: {
    color: ELIG_TOKENS.green,
    tint: ELIG_TOKENS.greenTint,
    title: 'Coverage active',
    detail: 'Active coverage is recorded for this plan.',
    Icon: CheckCircleOutlineRounded,
  },
  inactive: {
    color: ELIG_TOKENS.red,
    tint: ELIG_TOKENS.redTint,
    title: 'Coverage inactive',
    detail: 'This plan is recorded as inactive.',
    Icon: HighlightOffRounded,
  },
  unknown: {
    color: ELIG_TOKENS.amber,
    tint: ELIG_TOKENS.amberTint,
    title: 'Could not confirm coverage',
    detail: 'The recorded check did not establish a coverage result.',
    Icon: WarningAmberRounded,
  },
};

export function CoverageRecordSummary({
  current,
  children,
  benefitSupplement,
}: {
  current: EligibilityCurrentResponse;
  children?: ReactNode;
  benefitSupplement?: ReactNode;
}) {
  const [details, setDetails] = useState(false);
  const detailsId = useId();
  const empty = current.state === 'empty';
  const outcome = outcomes[current.coverage_result ?? 'unknown'];
  const showBenefits =
    current.coverage_result === 'active' ||
    FINANCIAL_FIELDS.some(
      field => current.benefits?.[field.apiKey] != null || !!current.provenance?.[field.apiKey],
    );
  const field = (definition: FinancialFieldDef) => {
    const value = current.benefits?.[definition.apiKey];
    const source = current.provenance?.[definition.apiKey];
    return (
      <Box
        key={definition.apiKey}
        role="group"
        aria-label={`${definition.label} benefit`}
        sx={{
          border: '1px solid',
          borderColor: ELIG_TOKENS.lineStrong,
          borderRadius: 1.5,
          minWidth: 0,
          px: 1.5,
          py: 1.25,
        }}
      >
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}
        >
          <Typography sx={{ color: ELIG_TOKENS.inkSoft, fontSize: 10.5, fontWeight: 650 }}>
            {definition.label}:
          </Typography>
          {source && (
            <CoverageSourceDetails fieldLabel={definition.label} source={source} compact />
          )}
        </Stack>
        <Typography sx={{ color: ELIG_TOKENS.ink, fontSize: 14, fontWeight: 750, mt: 0.25 }}>
          {value == null
            ? 'Unknown'
            : definition.prefix === '%'
              ? `${value}%`
              : formatUsdCents(value)}
        </Typography>
        {value == null && source?.kind === 'manual' && (
          <Typography sx={{ fontSize: 10.5, color: ELIG_TOKENS.inkSoft, mt: 0.5 }}>
            Manually cleared
          </Typography>
        )}
      </Box>
    );
  };
  return (
    <Box sx={{ borderLeft: empty ? undefined : `4px solid ${outcome.color}` }}>
      {empty ? (
        <Box sx={{ px: { xs: 2, md: 2.5 }, pt: 2.5 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 760 }}>
            No eligibility details recorded
          </Typography>
          <Typography sx={{ color: ELIG_TOKENS.inkSoft, fontSize: 13, mt: 0.6 }}>
            Check through JotPsych or record what the payer told you. Both paths save to this
            coverage history.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ bgcolor: outcome.tint, px: { xs: 2, md: 2.5 }, py: 2 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
            <outcome.Icon sx={{ color: outcome.color, fontSize: 21, mt: 0.1 }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: outcome.color, fontSize: 16, fontWeight: 800 }}>
                {outcome.title}
              </Typography>
              <Typography sx={{ color: ELIG_TOKENS.inkSoft, fontSize: 12.5, mt: 0.2 }}>
                {outcome.detail}
              </Typography>
            </Box>
            {current.provenance?.coverage_result && (
              <CoverageSourceDetails
                fieldLabel="Coverage result"
                source={current.provenance.coverage_result}
                compact
              />
            )}
          </Stack>
        </Box>
      )}
      <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2.25 }}>
        {showBenefits ? (
          <>
            <Box
              sx={{
                display: 'grid',
                gap: 1,
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  lg: `repeat(${benefitSupplement ? 5 : 4}, minmax(0, 1fr))`,
                },
              }}
            >
              {FINANCIAL_FIELDS.filter(item => primary.includes(item.field)).map(field)}
              {benefitSupplement && (
                <Box
                  sx={{ gridColumn: { xs: '1 / -1', lg: 'auto' }, display: 'flex', minWidth: 0 }}
                >
                  {benefitSupplement}
                </Box>
              )}
            </Box>
            <Button
              size="small"
              color="inherit"
              aria-expanded={details}
              aria-controls={detailsId}
              onClick={() => setDetails(value => !value)}
              sx={{ px: 0, mt: 0.75, fontSize: 11.5, color: ELIG_TOKENS.inkSoft }}
            >
              {details ? 'Fewer benefit details' : 'More benefit details'}
            </Button>
            <Collapse in={details} id={detailsId}>
              <Box
                sx={{
                  display: 'grid',
                  gap: 1,
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  mt: 1,
                }}
              >
                {FINANCIAL_FIELDS.filter(item => !primary.includes(item.field)).map(field)}
              </Box>
            </Collapse>
          </>
        ) : (
          !empty && (
            <Typography sx={{ color: ELIG_TOKENS.inkSoft, fontSize: 13 }}>
              No benefit amounts recorded. Follow up with the payer when more information is
              available.
            </Typography>
          )
        )}
        {!showBenefits && benefitSupplement}
        {children}
      </Box>
    </Box>
  );
}
